import { useAuth0, authMethodLabel } from "ct-auth"
import { useCallback, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { isManager, isSponsor, isTrainer, userOrg, canManageChildOrgs } from "../../../data-utils/authUtils.js"
import { clearRoleAssumption, getRoleAssumption } from "../../../data-utils/roleAssumptionStore.js"
import { useRoleAssumption } from "../../../hooks/useRoleAssumption.js"
import { UiActions } from "../../../store/ui-slice"
import { ADMIN_BASE_URL, DEBUG_FEATURES, ROUTE_PATHS } from "../../../utilities/constants"
import ErrorBoundary from "../ErrorBoundary/ErrorBoundary.jsx"
import RoleAssumptionModal from "../../RoleAssumption/RoleAssumptionModal.jsx"

// Compact remaining-time formatter for the dropdown chip. Tighter than
// the banner's "Xm YYs" because the dropdown is a glance, not a constant
// indicator. Ceiling up so 29:01 reads "30m" — slightly generous, but
// avoids the surprise of jumping from "29m" to "0m" inside one minute.
const formatRemainingShort = (ms) => {
  if (ms <= 0) return "expired"
  if (ms < 60_000) return "<1m"
  const totalMin = Math.ceil(ms / 60_000)
  if (totalMin < 60) return `${totalMin}m`
  const hours = Math.floor(totalMin / 60)
  const mins = totalMin % 60
  return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`
}

const titleCase = (s) =>
  s
    .replace(/[._-]+/g, " ")
    .trim()
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ")

/**
 * A human display name for the rail footer. Auth0 sets `name` = email for
 * password-login users, so "prefer name" alone still shows an email. Chain:
 * given/family name → `name` when it isn't an email → the email local-part with
 * any +tag stripped, title-cased ("pramod+admindev@…" → "Pramod"). The full email
 * stays in the trigger tooltip and the dropdown summary, so accounts that differ
 * only by +tag remain distinguishable one click away.
 */
export const displayNameForUser = (user) => {
  if (!user) return ""
  const given = (user.given_name || "").trim()
  const family = (user.family_name || "").trim()
  if (given || family) return [given, family].filter(Boolean).join(" ")
  const name = (user.name || "").trim()
  if (name && !name.includes("@")) return name
  const local = ((user.email || name || "").split("@")[0] || "").split("+")[0]
  return local ? titleCase(local) : user.email || ""
}

/**
 * UserMenu — the account menu that lives in the sidebar footer (CT Modern shell,
 * matching the company's other product). Moved verbatim from the old top Header:
 * user summary (org/role/login), Company Details, Admin Console, role assumption
 * (assume/stop + remaining time), dev tools, and Sign out.
 *
 * Renders as a Bootstrap drop-UP since it sits at the bottom of the rail.
 * `expanded` collapses the label when the rail is collapsed.
 */
function UserMenu({ expanded = true }) {
  const { logout, user } = useAuth0()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const showDevTools = useSelector((state) => state.ui.showDevTools)

  // Role-assumption state — read sessionStorage once per render so the two derived
  // booleans always see the same snapshot (see the old Header comment for history).
  const [assumeRoleOpen, setAssumeRoleOpen] = useState(false)
  const assumption = getRoleAssumption()
  const isAssumingRole = !!assumption
  // Managers (any org) and partner managers (partner-org SPONSORS, via the manage-child-orgs
  // grant) can assume a role. Match the server gate exactly (manager | sponsor): a partner
  // TRAINER also holds the manage-child grant but the server rejects a trainer caller, so
  // require the sponsor role here too — otherwise a trainer sees a dead-end button.
  const canAssumeRole = !assumption && (isManager(user) || (isSponsor(user) && canManageChildOrgs(user)))
  const { remainingMs } = useRoleAssumption()

  const handleStopAssumption = () => {
    try {
      clearRoleAssumption()
    } catch {
      // store self-defends; ignore
    }
    try {
      window.location.reload()
    } catch {
      // jsdom / SSR — best-effort
    }
  }

  /**
   * Clears the role-assumption sessionStorage synchronously before the Auth0
   * logout redirect — otherwise the assumption survives into the next login on
   * the same tab (see the detailed history in the pre-move Header component).
   */
  const handleLogoutClick = () => {
    try {
      clearRoleAssumption()
    } catch {
      // store self-defends; ignore
    }
    logout({
      logoutParams: {
        returnTo: window.__RUNTIME_CONFIG__.REACT_APP_AUTH0_CALLBACK_URL,
      },
    })
  }

  const handleCompanyDetailsClick = (e) => {
    e.preventDefault()
    navigate(ROUTE_PATHS.COMPANY_DETAILS)
  }

  const handleAdminConsoleClick = (e) => {
    e.preventDefault()
    window.location.replace(ADMIN_BASE_URL)
  }

  const toggleShowDevTools = useCallback(() => {
    dispatch(UiActions.setShowDevTools(!showDevTools))
  }, [showDevTools, dispatch])

  return (
    <>
      {canAssumeRole && (
        <ErrorBoundary fallback={null}>
          <RoleAssumptionModal open={assumeRoleOpen} onClose={() => setAssumeRoleOpen(false)} />
        </ErrorBoundary>
      )}
      <div className="dropup user-menu-footer">
        <button type="button" className="btn dropdown-toggle user-menu-trigger c-font-12" data-bs-toggle="dropdown" aria-expanded="false">
          <img src={user?.picture ?? "images/people-male.png"} alt="profile-icon" className="rounded-circle profile-image" width="30" height="30" />
          {expanded && (
            <span className="user-menu-id">
              {/* Class stays `user-menu-email` (styled in 3 shells); the CONTENT is now
                  the display name — the raw email lives in the tooltip + dropdown. */}
              <span className="user-menu-email" title={user?.email ?? user?.name ?? ""}>
                {displayNameForUser(user)}
              </span>
              {userOrg(user) && <span className="user-menu-org">Org: {userOrg(user)}</span>}
            </span>
          )}
        </button>
        {/* No inline min-width: the drop-up is width-constrained by each shell's rail
            styles so it never pokes out past the side nav. */}
        <ul className="dropdown-menu text-small user-menu-dropdown">
          <li className="px-3 py-2 dropdown-user-summary">
            <div className="dropdown-user-name">{displayNameForUser(user)}</div>
            <div className="dropdown-user-email" title={user?.email}>
              {user?.email}
            </div>
            <table className="dropdown-user-table">
              <tbody>
                {userOrg(user) && (
                  <tr>
                    <td className="label-cell">Org</td>
                    <td>{userOrg(user)}</td>
                  </tr>
                )}
                <tr>
                  <td className="label-cell">Role</td>
                  <td>{isManager(user) ? "Manager" : isSponsor(user) ? "Administrator" : isTrainer(user) ? "Training Admin" : "User"}</td>
                </tr>
                <tr>
                  <td className="label-cell">Login</td>
                  <td>{authMethodLabel(user)}</td>
                </tr>
              </tbody>
            </table>
          </li>
          {isSponsor(user) && !isManager(user) ? (
            <li>
              <button className="dropdown-item" onClick={handleCompanyDetailsClick}>
                <span className="icon-wrapper me-2">
                  <i className="fa-solid fa-building"></i>
                </span>
                Company Details
              </button>
            </li>
          ) : (
            <></>
          )}
          {isManager(user) ? (
            <li>
              <button className="dropdown-item" onClick={handleAdminConsoleClick}>
                <span className="icon-wrapper me-2">
                  <i className="fa-solid fa-cogs"></i>
                </span>
                Admin Console
              </button>
            </li>
          ) : (
            <></>
          )}
          {isManager(user) ? (
            <li>
              <button className="dropdown-item" onClick={() => navigate(ROUTE_PATHS.EVALUATIONS)}>
                <span className="icon-wrapper me-2">
                  <i className="fa-solid fa-flask"></i>
                </span>
                Evaluations
              </button>
            </li>
          ) : (
            <></>
          )}
          {canAssumeRole ? (
            <li>
              <button className="dropdown-item" onClick={() => setAssumeRoleOpen(true)}>
                <span className="icon-wrapper me-2">
                  <i className="fa-solid fa-user-secret"></i>
                </span>
                Assume role
              </button>
            </li>
          ) : (
            <></>
          )}
          {isAssumingRole ? (
            <li>
              <button className="dropdown-item d-flex align-items-center" onClick={handleStopAssumption}>
                <span className="icon-wrapper me-2">
                  <i className="fa-solid fa-user-slash"></i>
                </span>
                <span className="d-flex flex-column">
                  <span style={{ fontWeight: 500 }}>Stop assuming role</span>
                  <span
                    className="dropdown-secondary-status"
                    style={{
                      fontVariantNumeric: "tabular-nums",
                      fontSize: "0.72rem",
                      lineHeight: 1.2,
                      marginTop: "2px",
                    }}
                  >
                    Expires in {formatRemainingShort(remainingMs)}
                  </span>
                </span>
              </button>
            </li>
          ) : (
            <></>
          )}
          {DEBUG_FEATURES ? (
            <li>
              <button className="dropdown-item" onClick={toggleShowDevTools}>
                <span className="icon-wrapper me-2">
                  <i className="fa-solid fa-cogs"></i>
                </span>
                {showDevTools ? "Hide Dev Tools" : "Show Dev Tools"}
              </button>
            </li>
          ) : (
            <></>
          )}
          <li>
            <button className="dropdown-item" onClick={() => navigate(ROUTE_PATHS.HELP)}>
              <span className="icon-wrapper me-2">
                <i className="fa-regular fa-compass"></i>
              </span>
              Help Center
            </button>
          </li>
          <li>
            <button className="dropdown-item" onClick={() => window.dispatchEvent(new CustomEvent("ct:open-help"))}>
              <span className="icon-wrapper me-2">
                <i className="fa-regular fa-circle-question"></i>
              </span>
              Help on this page
            </button>
          </li>
          <li>
            <button className="dropdown-item logout" onClick={handleLogoutClick}>
              <span className="icon-wrapper me-2">
                <i className="fa-solid fa-right-from-bracket"></i>
              </span>
              Log out
            </button>
          </li>
        </ul>
      </div>
    </>
  )
}

export default UserMenu
